import { DOCUMENT } from '@angular/common';
import {
    ApplicationRef,
    DestroyRef,
    Inject,
    Injectable,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
    SwUpdate,
    VersionEvent,
    VersionInstallationFailedEvent,
    VersionReadyEvent,
} from '@angular/service-worker';
import {
    EMPTY,
    catchError,
    exhaustMap,
    filter,
    first,
    from,
    fromEvent,
    interval,
    map,
    merge,
    of,
    switchMap,
    throttleTime,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PwaUpdateDialogComponent } from '../../components/shared/pwa-update-dialog/pwa-update-dialog.component';
import {
    IPwaUpdateDialogData,
    IPwaVersionAppData,
} from '../../interfaces/pwa-update.interfaces';

const UPDATE_CHECK_INTERVAL_MS = 30 * 60 * 1000;
const UPDATE_CHECK_THROTTLE_MS = 60 * 1000;
const DISMISSED_UPDATE_KEY = 'pwaDismissedUpdateHash';

@Injectable({
    providedIn: 'root',
})
export class PwaUpdateService {
    private initialized = false;
    private dialogRef: MatDialogRef<PwaUpdateDialogComponent, boolean> | null =
        null;

    public constructor(
        private readonly swUpdate: SwUpdate,
        private readonly dialog: MatDialog,
        private readonly applicationRef: ApplicationRef,
        private readonly destroyRef: DestroyRef,
        @Inject(DOCUMENT) private readonly document: Document,
    ) {}

    public initialize(): void {
        if (this.initialized || !this.swUpdate.isEnabled) {
            return;
        }

        this.initialized = true;
        this.observeVersionUpdates();
        this.observeUnrecoverableState();
        this.startUpdateChecks();
    }

    private observeVersionUpdates(): void {
        this.swUpdate.versionUpdates
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => this.handleVersionEvent(event));
    }

    private observeUnrecoverableState(): void {
        this.swUpdate.unrecoverable
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((event) => {
                console.error('PWA entered an unrecoverable state', event.reason);
                this.openReloadDialog({ required: true });
            });
    }

    private startUpdateChecks(): void {
        const windowRef = this.document.defaultView;
        if (!windowRef) {
            return;
        }

        const visibilityChanges$ = fromEvent(
            this.document,
            'visibilitychange',
        ).pipe(filter(() => this.document.visibilityState === 'visible'));

        this.applicationRef.isStable
            .pipe(
                filter(Boolean),
                first(),
                switchMap(() =>
                    merge(
                        of(undefined),
                        interval(UPDATE_CHECK_INTERVAL_MS).pipe(
                            map(() => undefined),
                        ),
                        fromEvent(windowRef, 'online').pipe(
                            map(() => undefined),
                        ),
                        visibilityChanges$.pipe(map(() => undefined)),
                    ),
                ),
                throttleTime(UPDATE_CHECK_THROTTLE_MS),
                filter(() => windowRef.navigator.onLine),
                exhaustMap(() =>
                    from(this.swUpdate.checkForUpdate()).pipe(
                        catchError((error: unknown) => {
                            console.error(
                                'Не удалось проверить обновление PWA',
                                error,
                            );
                            return EMPTY;
                        }),
                    ),
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe();
    }

    private handleVersionEvent(event: VersionEvent): void {
        if (event.type === 'VERSION_READY') {
            this.handleReadyVersion(event);
            return;
        }

        if (event.type === 'VERSION_INSTALLATION_FAILED') {
            this.handleInstallationFailure(event);
        }
    }

    private handleReadyVersion(event: VersionReadyEvent): void {
        if (
            this.dialogRef ||
            this.getDismissedUpdateHash() === event.latestVersion.hash
        ) {
            return;
        }

        this.openReloadDialog(
            {
                currentVersion: this.getVersion(event.currentVersion.appData),
                latestVersion: this.getVersion(event.latestVersion.appData),
                required: false,
            },
            event.latestVersion.hash,
        );
    }

    private handleInstallationFailure(
        event: VersionInstallationFailedEvent,
    ): void {
        console.error(
            'Не удалось установить новую версию PWA',
            event.version.hash,
            event.error,
        );
    }

    private openReloadDialog(
        data: IPwaUpdateDialogData,
        updateHash?: string,
    ): void {
        this.dialogRef?.close(false);
        const dialogRef = this.dialog.open(PwaUpdateDialogComponent, {
            width: 'calc(100vw - 2rem)',
            maxWidth: '28rem',
            disableClose: data.required,
            closeOnNavigation: false,
            autoFocus: 'first-tabbable',
            restoreFocus: !data.required,
            ariaLabel: data.required
                ? 'Требуется перезагрузка приложения'
                : 'Доступно обновление приложения',
            data,
        });
        this.dialogRef = dialogRef;

        dialogRef
            .afterClosed()
            .pipe(first(), takeUntilDestroyed(this.destroyRef))
            .subscribe((shouldReload) => {
                if (this.dialogRef === dialogRef) {
                    this.dialogRef = null;
                }

                if (shouldReload) {
                    this.document.location.reload();
                    return;
                }

                if (updateHash) {
                    this.setDismissedUpdateHash(updateHash);
                }
            });
    }

    private getVersion(appData?: object): string | undefined {
        const version = (appData as IPwaVersionAppData | undefined)?.version;
        return typeof version === 'string' && version.length > 0
            ? version
            : undefined;
    }

    private getDismissedUpdateHash(): string | null {
        try {
            return this.document.defaultView?.sessionStorage.getItem(
                DISMISSED_UPDATE_KEY,
            ) ?? null;
        } catch {
            return null;
        }
    }

    private setDismissedUpdateHash(updateHash: string): void {
        try {
            this.document.defaultView?.sessionStorage.setItem(
                DISMISSED_UPDATE_KEY,
                updateHash,
            );
        } catch {
            // Storage can be unavailable in privacy-restricted browser modes.
        }
    }
}
